import React from "react";
import UseContext from "hooks/useContext";
import { CrossIcon } from "assets/icons";
import { TASK_NOTICE } from "pages/missionTemplates/utils/templateTasks";

// One pass, so a label containing "{label}" or "$&" is shown as typed.
const fillPlaceholder = (template, name, value) =>
    template.replace(`{${name}}`, () => value || "");

/** The message under the add field of the Tasks step (`templateData.taskNotice`). */
const TaskNotice = ({
    notice,
    showAddLink,
    onAdd,
    canAddLine,
    onAddLine,
    onClose,
}) => {
    const { language } = UseContext();
    const t = (key, fallback) =>
        language.getValue(`missionTemplate.tasks.${key}`, fallback);

    // Text of a notice, or of one line of a MULTIPLE notice.
    const messageOf = (item) => {
        switch (item?.type) {
            case TASK_NOTICE.TOO_MANY:
                return t("tooMany", "Le nombre maximum de tâches est atteint.");
            case TASK_NOTICE.REJECTED:
                return fillPlaceholder(
                    t(
                        "rejected",
                        "« {label} » n'a pas été ajoutée : ce texte ne décrit pas une tâche du poste."
                    ),
                    "label",
                    item.label
                );
            case TASK_NOTICE.UNCHECKED_TOO_LONG:
                return fillPlaceholder(
                    t(
                        "uncheckedTooLong",
                        "« {label} » n'a pas pu être vérifiée : ajoutez les tâches une par une (80 caractères au plus)."
                    ),
                    "label",
                    item.label
                );
            case TASK_NOTICE.TICKED:
                return fillPlaceholder(
                    t("existingTicked", "« {match} » existe déjà : elle a été cochée."),
                    "match",
                    item.match
                );
            case TASK_NOTICE.ALREADY_LISTED:
                return fillPlaceholder(
                    t(
                        "existingListed",
                        "« {match} » existe déjà dans la liste : elle n'a pas été ajoutée."
                    ),
                    "match",
                    item.match
                );
            default:
                return "";
        }
    };

    const renderAddLink = (onClick) => (
        <>
            {" "}
            <button
                type="button"
                onClick={onClick}
                className="underline hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
            >
                {t("addAsNew", "Ajouter comme nouvelle tâche")}
            </button>
        </>
    );

    // Grey banner with one cross for its whole content.
    const renderBanner = (content) => {
        const closeLabel = t("closeLabel", "Fermer");
        return (
            <div
                className="flex items-start justify-between gap-2 rounded-[0.4rem] border border-gray-100 bg-gray-50 py-2 pl-3 pr-1"
                role="status"
            >
                {content}
                <button
                    type="button"
                    onClick={onClose}
                    aria-label={closeLabel}
                    className="shrink-0 p-1 rounded-md hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                >
                    <CrossIcon wh={14} color="#6B7280" />
                </button>
            </div>
        );
    };

    switch (notice?.type) {
        case TASK_NOTICE.TOO_MANY:
        case TASK_NOTICE.REJECTED:
        case TASK_NOTICE.UNCHECKED_TOO_LONG:
            return (
                <p className="ft-sm text-red-500" role="alert">
                    {messageOf(notice)}
                </p>
            );
        case TASK_NOTICE.TICKED:
        case TASK_NOTICE.ALREADY_LISTED:
            return renderBanner(
                <p className="ft-sm text-gray-500 min-w-0 break-words py-0.5">
                    {messageOf(notice)}
                    {showAddLink && renderAddLink(onAdd)}
                </p>
            );
        case TASK_NOTICE.MULTIPLE: {
            const lines = Array.isArray(notice.lines) ? notice.lines : [];
            if (lines.length === 0) return null;
            return renderBanner(
                <ul className="ft-sm text-gray-500 min-w-0 break-words py-0.5 flex flex-col gap-1">
                    {lines.map((line) => {
                        const isError =
                            line.type === TASK_NOTICE.REJECTED ||
                            line.type === TASK_NOTICE.TOO_MANY;
                        return (
                            <li
                                key={line.id}
                                className={isError ? "text-red-500" : undefined}
                            >
                                {messageOf(line)}
                                {canAddLine?.(line) &&
                                    renderAddLink(() => onAddLine(line.id))}
                            </li>
                        );
                    })}
                </ul>
            );
        }
        default:
            return null;
    }
};

export default TaskNotice;
